import React, { useEffect, useState, useRef } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  CardMedia,
  CircularProgress,
  AppBar,
  Toolbar,
  Button,
  IconButton,
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {mediaService} from '../services/api.ts';
import LogoutIcon from '@mui/icons-material/Logout';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useAuth } from 'react-oidc-context';
import VideoFileIcon from '@mui/icons-material/VideoFile';
import ReactHlsPlayer from 'react-hls-player';
import Hls from 'hls.js';
import CloseIcon from '@mui/icons-material/Close';
import { useAuthenticatedAxios } from '../services/useAuthenticatedAxios.js';
import { decodeIdToken } from '../utils/tokenUtils.ts';
import { signOutRedirect } from '../utils/authUtils.ts';

interface MediaData {
  mediaId: string;
  mediaName: string;
  mediaDescription: string;
  mediaPicUrl: string | null;
}

const StyledCard = styled(Card)(({ theme }) => ({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  transition: 'transform 0.2s ease-in-out',
  '&:hover': {
    transform: 'scale(1.02)',
    boxShadow: theme.shadows[4],
  },
}));

const IconWrapper = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  padding: theme.spacing(2),
  color: theme.palette.primary.main,
  '& svg': {
    fontSize: '4rem',
  },
}));

const CardMediaWrapper = styled(Box)(({ theme }) => ({
  height: 200,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: theme.palette.grey[100],
  color: theme.palette.grey[500],
  '& svg': {
    fontSize: '4rem',
  },
}));

const VideoDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    width: '90%',
    maxWidth: '1200px',
    margin: '16px',
    backgroundColor: '#000',
  },
}));

const MediaPage = () => {
  const [mediaList, setMediaList] = useState<MediaData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedVideo, setSelectedVideo] = useState<MediaData | null>(null);
  const [videoUrl, setVideoUrl] = useState<string | null>(null);
  const [isVideoLoading, setIsVideoLoading] = useState(false);
  const auth = useAuth();
  const playerRef = useRef<HTMLVideoElement>(null);
  const api = useAuthenticatedAxios();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const playlistId = searchParams.get('playlist');

  useEffect(() => {
    fetchMediaList();
  }, [playlistId]);

  useEffect(() => {
    if (videoUrl && playerRef.current) {
      const hls = new Hls({
        xhrSetup: (xhr) => {
          xhr.withCredentials = true; // Enable sending cookies with requests
        }
      });
      
      hls.loadSource(videoUrl);
      hls.attachMedia(playerRef.current);
      
      return () => {
        hls.destroy();
      };
    }
  }, [videoUrl]);

  useEffect(() => {
    if (auth.user?.id_token) {
      const decodedToken = decodeIdToken(auth.user.id_token);
      console.log('Decoded token:', decodedToken);
      console.log('Access token:', decodeIdToken(auth.user.access_token));
    }
  }, [auth.user?.id_token]);

  const fetchMediaList = async () => {
    try {
      if (!playlistId) {
        setError('No playlist ID provided');
        setLoading(false);
        return;
      }

      const response = await mediaService.listMediaByPlaylist(api, playlistId);
      if (response.success) {
        setMediaList(response.data);
        setError(null);
      } else {
        setError(response.message || 'Failed to fetch media list');
      }
    } catch (err) {
      setError('Failed to fetch media list. Please try again later.');
      console.error('Error fetching media:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleVideoClick = async (media: MediaData) => {
    try {
      setIsVideoLoading(true);
      const url = await mediaService.getMediaUrl(api, media.mediaName);
      setVideoUrl(url);
      setSelectedVideo(media);
    } catch (err) {
      console.error('Error fetching video URL:', err);
      setError('Failed to load video. Please try again later.');
    } finally {
      setIsVideoLoading(false);
    }
  };

  const handleCloseVideo = () => {
    setSelectedVideo(null);
    setVideoUrl(null);
  };

  const handleLogout = () => {
    auth.signoutRedirect();
  };

  const handleBackToPlaylists = () => {
    navigate('/playlists');
  };

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ flexGrow: 1 }}>
      <AppBar position="static">
        <Toolbar>
          <IconButton color="inherit" onClick={handleBackToPlaylists} sx={{ mr: 2 }}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            The French Journey - Media Library
          </Typography>
          <IconButton color="inherit" onClick={() => signOutRedirect(auth)}>
            <LogoutIcon />
          </IconButton>
        </Toolbar>
      </AppBar>

      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        {error ? (
          <Typography color="error" align="center" gutterBottom>
            {error}
          </Typography>
        ) : (
          <>
            <Typography variant="h4" gutterBottom>
              Available Media
            </Typography>
            <Grid container spacing={3}>
              {mediaList.map((media) => (
                <Grid item xs={12} sm={6} md={4} key={media.mediaId}>
                  <StyledCard>
                    {media.mediaPicUrl ? (
                      <CardMedia
                        component="img"
                        height="200"
                        image={media.mediaPicUrl}
                        alt={media.mediaName}
                      />
                    ) : (
                      <CardMediaWrapper>
                        <VideoFileIcon />
                      </CardMediaWrapper>
                    )}
                    <CardContent>
                      <Typography gutterBottom variant="h6" component="div" align="center">
                        {media.mediaName}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        {media.mediaDescription}
                      </Typography>
                      <Button
                        variant="contained"
                        color="primary"
                        fullWidth
                        onClick={() => handleVideoClick(media)}
                        disabled={isVideoLoading}
                      >
                        {isVideoLoading ? 'Loading...' : 'Watch Video'}
                      </Button>
                    </CardContent>
                  </StyledCard>
                </Grid>
              ))}
            </Grid>
          </>
        )}
      </Container>

      <VideoDialog
        open={Boolean(selectedVideo)}
        onClose={handleCloseVideo}
        maxWidth={false}
      >
        <DialogTitle sx={{ m: 0, p: 2, color: 'white' }}>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Typography variant="h6">
              {selectedVideo?.mediaName || ''}
            </Typography>
            <IconButton
              aria-label="close"
              onClick={handleCloseVideo}
              sx={{ color: 'white' }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          {videoUrl && (
            <ReactHlsPlayer
              playerRef={playerRef}
              src={videoUrl}
              autoPlay={true}
              controls={true}
              width="100%"
              height="auto"
              style={{ aspectRatio: '16/9' }}
              hlsConfig={{
                xhrSetup: (xhr) => {
                  xhr.withCredentials = true;
                }
              }}
            />
          )}
        </DialogContent>
      </VideoDialog>
    </Box>
  );
};

export default MediaPage; 